import React, { useState, useEffect, createRef, useCallback, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import { makeStyles, useTheme } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Input from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Chip from '@mui/material/Chip';

import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';

import { arrayMoveImmutable as arrayMove } from 'array-move';

import StyledPaper from '../../../components/styled/paper';
import AlertsService, { DefaultMapping, AllMapping } from '../../../data/alerts-service';
import DataContext from '../../../data/data-context';
import { blue } from '../../../data/colors';
import ListWrapper from '../components/column-selector';

const useStyles = makeStyles(theme => ({
    root: {
        zIndex: theme.zIndex.modal + 3,
        width: 775,
        minHeight: 500,
        top: '25px !important',
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500]
    },
    chip: {
        margin: theme.spacing(0.5),
        '&.hidden .MuiChip-label': {
            width: 150,
        },
        '&:not(.hidden), .MuiChip-label': {
            width: 134,
        },
    },
    grid: {
        display: 'grid',
        gridGap: 0,
        gridTemplateColumns: 'auto auto auto auto auto',
        width: 'auto',
        maxHeight: 200,
        whiteSpace: 'nowrap',
        border: 0,
        backgroundColor: 'transparent',
        overflow: 'auto',
        position: 'relative',
        zIndex: 0,
        borderRadius: 3,
        outline: 'none',
    },

    gridItem: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        outline: 'none',

        '&:focus': {
            textIndent: -2,
        },

        '& .wrapper': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            transform: 'scale(1)',
            span: {
                display: 'none',
            }
        }
    },
}));

const ColumnSelector = (props) => {
    const classes = useStyles();
    const [search, setSearch] = useState('');
    const [columns, setColumns] = useState([]);
    const [sort, setSort] = useState();
    const [availableColumns, setAvailableColumns] = useState([]);
    const container = useRef(null);

    const handleSearchChanged = event => {
        setSearch(event.target.value || '');
    };

    const isVisible = c => !c.hide && !c.columnSelectorHidden && inSearch(c);

    const inSearch = useCallback(c =>
        search.length < 0 ||
        (c.variable + c.title).toLowerCase().includes(search.toLowerCase()), [search]);

    const isNotvisible = useCallback(c =>
        !!c.hide && !c.columnSelectorHidden && inSearch(c), [inSearch]);

    const isSorted = c => c.field === sort.field ? sort.sort === 'desc' ? -1 : 1 : 0;

    const handleSortColumnChanged = c => {
        props.setSort({
            field: c.field,
            sort: sort.field === c.field && sort.sort === 'desc' ? 'asc' : 'desc'
        });
    };

    const handleSortEnded = (oldIndex, newIndex) => {
        // the provided indices are from filtered lists. Need to find the real index.
        const filtered = columns.filter(c => !c.hide && !c.columnSelectorHidden);
        const src = filtered[oldIndex];
        const dest = filtered[newIndex];
        const oi = columns.findIndex(c => c.variable === src.variable);
        const ni = columns.findIndex(c => c.variable === dest.variable);
        props.setColumns(arrayMove(columns, oi, ni));
    };

    const handleRemoveColumn = (c) => {
        const i = columns.findIndex(col => col.variable === c.variable);

        // bubble to the top of the hidden columns
        const lastvisible = columns.map(c => !c.hide && !c.columnSelectorHidden).lastIndexOf(true);
        columns[i].hide = true;
        const updated = arrayMove(columns, i, lastvisible - 1);
        // console.log(updated);
        props.setColumns(updated);

        // move sorting if needed
        if (sort.field === c.field) {
            let j = 0;
            let next = columns[j];
            while (j < columns.length - 1 && (!next.sortable || next.hide)) {
                j++;
                next = columns[j];
            }
            props.setSort({ field: next.field, sort: sort.sort });
        }
    };

    const handleAddColumn = (c) => {
        const i = columns.findIndex(col => col.variable === c.variable);

        // bubble to the buttom of the visible columns
        const lastvisible = columns.map(c => !c.hide && !c.columnSelectorHidden).lastIndexOf(true);
        columns[i].hide = false;
        props.setColumns(arrayMove(columns, i, lastvisible + 1));
    };

    useEffect(() => {
        // copy
        setColumns(props.columns || []);
    }, [props.columns]);

    useEffect(() => {
        setSort(props.sort);
    }, [props.sort]);

    useEffect(() => {
        setAvailableColumns((columns || [])
            .filter(isNotvisible)
            .sort((a, b) => (a.title).localeCompare(b.title))
        );
    }, [columns, isNotvisible]);

    return (
        <div ref={container}>
            <DialogContent dividers style={{ marginTop: 0, paddingTop: 10 }}>
                <Grid container spacing={1} direction="row" justifyContent="space-between">
                    <Grid item p={1} >
                        <Typography variant="subtitle1">
                                Selected Columns ({columns.filter(c => !c.hide).length})
                        </Typography>
                    </Grid>
                    <Grid item p={1} style={{ paddingBottom: 20 }}>
                        <Input
                            value={search}
                            onChange={handleSearchChanged}
                            placeholder="Search..."
                            variant="outlined"
                            margin="dense"
                            style={{ margin: 0 }}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">
                                    <React.Fragment>
                                        <IconButton
                                            disabled={!search}
                                            aria-label="clear"
                                            title="clear"
                                            onClick={() => setSearch('')}>
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                        <SearchIcon fontSize="inherit" />
                                    </React.Fragment>
                                </InputAdornment>
                            }}/>
                    </Grid>
                </Grid>
                <div>
                    <ListWrapper
                        helperContainer={container.current}
                        classes={classes}
                        axis={'xy'}
                        items={columns.filter(isVisible)}
                        className={classes.grid}
                        itemClass={classes.gridItem}
                        onAction={c => c.sortable && handleSortColumnChanged(c)}
                        onDelete={c => handleRemoveColumn(c)}
                        onSort={handleSortColumnChanged}
                        isSorted={isSorted}
                        isDisabled={false}
                        onSortEnd={handleSortEnded}
                        getColor={c => 'secondary'}/>
                </div>
            </DialogContent>
            <DialogContent dividers>
                <Grid item p={1}>
                    <Typography variant="subtitle1">
                            Available Columns ({columns.filter(isNotvisible).length})
                    </Typography>
                </Grid>
                <div style={{ paddingTop: 15 }}>
                    <ListWrapper
                        helperContainer={container.current}
                        classes={classes}
                        axis={'xy'}
                        items={availableColumns}
                        className={classes.grid}
                        itemClass={classes.gridItem}
                        onAction={handleAddColumn}
                        isDisabled={true}
                        getColor={c => 'primary'}/>
                </div>
            </DialogContent>
        </div>
    );
};
ColumnSelector.propTypes = {
    columns: PropTypes.array,
    sort: PropTypes.object,
    setSort: PropTypes.func,
    setColumns: PropTypes.func,
};

const ColumnMapper = (props) => {
    const classes = useStyles();
    const theme = useTheme();
    const [mappings, setMappings] = useState({});
    const [columns, setColumns] = useState([]);
    const [current, setCurrent] = useState(0);
    const [availableColumns, setAvailableColumns] = useState([]);
    const { alertMappings } = useContext(DataContext);
    const container = useRef(null);

    const handleSortEnded = (k, o, n) => {
        if (!mappings[k]) return;

        props.setMappings({
            ...mappings,
            [k]: arrayMove(mappings[k], o, n)
        });
    };

    const isAllowed = c => {
        const k = columns[current];
        const typ = c.type === AlertsService.columnsMap[k].type;
        const dup = mappings[k].find(cc => cc === c.variable) !== undefined;
        return typ && !dup;
    };

    const handleAddColumn = (c) => {
        if (columns.length <= current) return;

        const k = columns[current];
        if (!isAllowed(c)) return;

        props.setMappings({
            ...mappings,
            [k]: [...mappings[k], c.variable],
        });
    };

    const handleRemoveColumn = (k, c) => {
        if (!mappings[k]) return;

        const values = mappings[k].filter(cc => cc !== c.variable);
        // at least one value is required
        if (!values.length) values.push(k);

        props.setMappings({
            ...mappings,
            [k]: values,
        });
    };

    useEffect(() => {
        // all columns in alphabetical order
        setAvailableColumns(Object.entries(AllMapping)
            .reduce((a, [k, v]) => [...a, ...v], [])
            .map(k => AlertsService.columnsMap[k])
            .sort((a, b) => (a.title).localeCompare(b.title))
            // dedup with O(n) after sorting
            .filter((c, i, a) => !i || c !== a[i - 1]));
    }, [alertMappings]);

    useEffect(() => {
        setColumns(Object.keys(props.mappings || {}));
        setMappings(props.mappings || {});
    }, [props.mappings]);

    return (
        <div ref={container}>
            <DialogContent dividers style={{ marginTop: 0, paddingTop: 10 }}>
                <Grid container spacing={1} direction="row">
                    <Grid item p={1}>
                        <Typography variant="subtitle1">
                                Column Mapping
                        </Typography>
                    </Grid>
                </Grid>
                {/* column categories */}
                <Grid container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start">
                    {
                        columns.map((k, i) =>
                            <Grid key={k} container
                                style={{ width: 'auto' }}
                                direction="column"
                                justifyContent="flex-start"
                                alignItems="flex-start">
                                {/* column mapping category */}
                                <Grid item key={k} style={{ marginBottom: 10 }}>
                                    <Chip
                                        key={k}
                                        clickable={false}
                                        size='small'
                                        style={{
                                            backgroundColor: current === i
                                                ? theme.palette.primary.main
                                                : blue[100],
                                            color: current === i
                                                ? theme.palette.getContrastText(theme.palette.primary.main)
                                                : theme.palette.getContrastText(blue[100]),
                                            fontWeight: 'bold',
                                            marginBottom: 7,
                                        }}
                                        label={AlertsService.columnsMap[k].title}
                                        className={clsx(classes.chip)}
                                        onClick={() => setCurrent(i)}/>
                                </Grid>
                                {/* column mapping values÷ */}
                                <ListWrapper
                                    helperContainer={container.current}
                                    classes={classes}
                                    axis={'y'}
                                    lockAxis='y'
                                    items={(mappings[k] || []).map(c => AlertsService.columnsMap[c])}
                                    itemClass={classes.gridItem}
                                    getColor={c => 'primary'}
                                    chipStyle={{
                                        backgroundColor: current === i
                                            ? blue[400]
                                            : blue[100],
                                        color: current === i
                                            ? theme.palette.getContrastText(blue[400])
                                            : theme.palette.getContrastText(blue[100]),
                                    }}
                                    onDelete={c => handleRemoveColumn(k, c)}
                                    isDisabled={false}
                                    onSortEnd={(o, n) => handleSortEnded(k, o, n)}/>
                            </Grid>
                        )
                    }
                </Grid>
            </DialogContent>
            <DialogContent dividers>
                <Grid item p={1}>
                    <Typography variant="subtitle1">
                            Select Columns to Map
                    </Typography>
                </Grid>
                {/* available columns */}
                <div style={{ paddingTop: 15 }}>
                    <ListWrapper
                        helperContainer={container.current}
                        classes={classes}
                        items={availableColumns.filter(isAllowed)}
                        className={classes.grid}
                        itemClass={classes.gridItem}
                        onAction={c => handleAddColumn(c)}
                        getColor={c => 'primary'}
                        chipStyle={{
                            backgroundColor: blue[400],
                            color: theme.palette.getContrastText(blue[400]),
                        }}
                        isDisabled={true}/>
                </div>
            </DialogContent>
        </div>
    );
};
ColumnMapper.propTypes = {
    mappings: PropTypes.object,
    setMappings: PropTypes.func,
};

const ColumnMain = (props) => {
    const classes = useStyles();
    const dialogRef = createRef();
    const [columns, setColumns] = useState([]);
    const [sort, setSort] = useState({});
    const [mappings, setMappings] = useState({});
    const [tab, setTab] = useState('select');

    function handleKeyDown (event) {
        if (event.key === 'Escape') {
            event.preventDefault();
            props.onClickAway();
        }
    }

    const handleOnOK = () => {
        props.onOK({ columns, sort, mappings });
        props.onClickAway();
    };

    const handleOnDefaults = () => {
        const currentmap = columns.reduce((a, c) => ({ ...a, [c.variable]: c }), {});
        const cols = AlertsService.fields
            .filter(f => !f.tableHidden)
            .map(f => {
                const { order, hide, ...rest } = currentmap[f.variable];
                return {
                    ...f,
                    ...rest,
                    hide: f.addon ? rest.columnSelectorHidden : f.hide
                };
            })
            .sort((a, b) => {
                if (a.hide && !b.hide) return 1;
                if (b.hide && !a.hide) return -1;
                if (a.order === undefined && b.order === undefined) {
                    return a.variable.localeCompare(b.variable);
                }
                return (a.order || 0) - (b.order || 0);
            });
        setColumns(cols);
    };

    const handleOnReset = () => {
        setMappings({ ...DefaultMapping });
    };

    useEffect(() => {
        // copy
        setColumns(props.columns.map(c => ({ ...c })));
    }, [props.columns]);

    useEffect(() => {
        // copy
        setMappings({ ...props.mappings || {} });
    }, [props.mappings]);

    useEffect(() => {
        setSort({ field: props.sortCol, sort: props.sortOrder });
    }, [props.sortCol, props.sortOrder]);

    return (
        <Popper
            className={classes.root}
            open={props.show}
            anchorEl={props.anchorEl}
            placement={'top-end'}
            modifiers={[
                { name: 'flip', enabled: false, options: { altBoundary: true, rootBoundary: 'document' } },
                { name: 'preventOverflow', enabled: true, options: { tether: true, rootBoundary: 'document' } },
            ]}
            role={undefined} transition>
            {({ TransitionProps, placement }) => (
                <StyledPaper ref={dialogRef}>

                    {/* dialog title */}
                    <div onKeyDown={handleKeyDown}>
                        <DialogTitle>
                            <Button aria-label="Column Mapping" onClick={() => setTab('select')} color="secondary">
                            Select Columns
                            </Button>
                            <Button aria-label="Column Mapping" onClick={() => setTab('map')} color="secondary">
                            Column Mapping
                            </Button>
                            <IconButton
                                aria-label="close"
                                className={classes.closeButton}
                                onClick={props.onClickAway}
                                size="large">
                                <CloseIcon fontSize="small"/>
                            </IconButton>
                        </DialogTitle>
                    </div>

                    {/* dialog content */}
                    <div>
                        {{
                            select: <ColumnSelector
                                columns={columns}
                                sort={sort}
                                setSort={setSort}
                                setColumns={setColumns}/>,
                            map: <ColumnMapper
                                mappings={mappings}
                                setMappings={setMappings}/>,
                        }[tab]}
                    </div>

                    {/* dialog actions */}
                    <DialogActions>
                        {tab === 'select' &&
                            <div style={{ flex: '1 0 0' }}>
                                <Button aria-label="Defaults" onClick={handleOnDefaults} color="primary">
                                    Defaults
                                </Button>
                            </div>
                        }
                        {tab === 'map' &&
                            <div style={{ flex: '1 0 0' }}>
                                <Button aria-label="Reset" onClick={handleOnReset} color="primary">
                                    Reset
                                </Button>
                            </div>
                        }
                        <Button autoFocus aria-label="Cancel" onClick={props.onClickAway} color="primary">
                            Cancel
                        </Button>
                        <Button aria-label="OK" onClick={handleOnOK} color="primary">
                            OK
                        </Button>
                    </DialogActions>

                </StyledPaper>
            )}
        </Popper>
    );
};

ColumnMain.propTypes = {
    show: PropTypes.bool.isRequired,
    placement: PropTypes.string,
    anchorEl: PropTypes.object,
    onClickAway: PropTypes.func.isRequired,
    columns: PropTypes.array,
    mappings: PropTypes.object,
    sortCol: PropTypes.string,
    sortOrder: PropTypes.string,
    onOK: PropTypes.func
};

export {
    ColumnMain as default,
};
